import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { config } from '../config/config'
import type { User } from '../models'

export type UserStore = {
  user: User | null
  persist: boolean
  /** Set only when authentication can no longer be recovered by refreshing tokens. */
  sessionExpired: boolean
  onRefreshTokenError?: (error: unknown) => void
  onInitError?: (error: unknown) => void
  onSignInError?: (error: unknown) => void
}

export const useUserStore = create<UserStore>()(
  immer(() => ({
    user: null,
    persist: true,
    sessionExpired: false,
  })),
)

export async function setUser(user: User | null) {
  useUserStore.setState((draft) => {
    draft.user = user
    if (user) {
      draft.sessionExpired = false
    }
  })

  if (useUserStore.getState().persist) {
    await config.getSaveKv()('user', JSON.stringify(user))
  }

  return user
}

/**
 * Ends a session because its credentials can no longer be refreshed.
 *
 * This deliberately differs from `setUser(null)`: a user-initiated logout
 * should clear credentials without showing the session-expired experience.
 */
export async function expireUserSession(): Promise<void> {
  if (useUserStore.getState().sessionExpired) return

  useUserStore.setState((draft) => {
    draft.user = null
    draft.sessionExpired = true
  })

  if (useUserStore.getState().persist) {
    await config.getSaveKv()('user', JSON.stringify(null))
  }
}

export async function initUserStore() {
  if (!useUserStore.getState().persist) return

  const user = await config.getLoadKv()('user')
  if (!user) return

  try {
    await setUser(JSON.parse(user) as User | null)
  } catch (error) {
    useUserStore.getState().onInitError?.(error)
  }
}

export function useUser() {
  return useUserStore((v) => v.user)
}
